import {
  createEmptyClip,
  emptyCell,
  patternSteps,
  type ClipIR,
  type Pattern,
  type PatternCell,
} from "../../../../src/lib/audio";

/** Active autocomplete token: text after the last whitespace/comma. */
export function activeTagToken(text: string): { before: string; token: string } {
  const match = /^(.*?(?:[\s,]+|^))([^\s,]*)$/.exec(text);
  if (!match) {
    return { before: "", token: text };
  }
  return { before: match[1] ?? "", token: match[2] ?? "" };
}

/** Ranked pattern suggestions for a typed token (name / id / bare number). */
export function filterPatternSuggestions(clip: ClipIR, token: string): Pattern[] {
  const q = token.trim().toLowerCase();
  if (!q) {
    return [];
  }
  const scored: { pattern: Pattern; score: number }[] = [];
  for (const pattern of clip.patterns) {
    const id = pattern.id.toLowerCase();
    const name = (pattern.name ?? "").trim().toLowerCase();
    let score = -1;
    if (name.startsWith(q) || id.startsWith(q)) {
      score = 0;
    } else if (name.includes(q) || id.includes(q)) {
      score = 1;
    }
    if (score >= 0) {
      scored.push({ pattern, score });
    }
  }
  scored.sort((a, b) => {
    if (a.score !== b.score) {
      return a.score - b.score;
    }
    const an = (a.pattern.name ?? "").trim().toLowerCase() || a.pattern.id.toLowerCase();
    const bn = (b.pattern.name ?? "").trim().toLowerCase() || b.pattern.id.toLowerCase();
    return an.localeCompare(bn) || a.pattern.id.localeCompare(b.pattern.id);
  });
  return scored.slice(0, 12).map(entry => entry.pattern);
}

/** Next unused `P{n}` id for a clip. */
export function nextPatternId(clip: ClipIR): string {
  let n = clip.patterns.length;
  const used = new Set(clip.patterns.map(pattern => pattern.id));
  while (used.has(`P${n}`)) {
    n += 1;
  }
  return `P${n}`;
}

/** Empty pattern cloned from the first track names, at `steps` length. */
export function createBlankPattern(clip: ClipIR, id: string, steps: number): Pattern {
  const len = Math.max(1, Math.round(steps));
  const sourceTracks = clip.patterns[0]?.tracks ?? [];
  const trackCount = Math.max(1, sourceTracks.length);
  const template = createEmptyClip({
    steps: len,
    stepDivision: clip.stepDivision,
    bpm: clip.bpm,
    trackCount,
  });
  const pattern = template.patterns[0]!;
  pattern.id = id;
  for (let i = 0; i < trackCount; i++) {
    const track = pattern.tracks[i];
    if (!track) {
      continue;
    }
    const name = sourceTracks[i]?.name;
    if (name) {
      track.name = name;
    }
    track.cells = Array.from({ length: len }, () => emptyCell());
  }
  pattern.steps = len;
  return pattern;
}

/**
 * Treat `clip.patterns` array order as the linear arrangement.
 * Mirrors into a single scene `S0` so project bounce / song expand stay in sync.
 */
export function syncLinearArrangement(clip: ClipIR): void {
  const order = clip.patterns.map(pattern => pattern.id);
  const existing = clip.scenes?.find(scene => scene.id === "S0");
  const sceneName = existing?.name;
  clip.scenes = [
    {
      id: "S0",
      ...(sceneName ? { name: sceneName } : {}),
      order: [...order],
    },
  ];
  clip.order = ["S0"];
}

/** Deep-clone a pattern under a fresh id (optional name suffix). */
export function clonePatternAsNew(
  clip: ClipIR,
  source: Pattern,
  options?: { nameSuffix?: string },
): Pattern {
  const next = structuredClone(source);
  next.id = nextPatternId(clip);
  const base = (source.name ?? "").trim();
  if (options?.nameSuffix) {
    next.name = (base ? `${base}${options.nameSuffix}` : `${source.id}${options.nameSuffix}`).slice(0, 48);
  } else if (base) {
    // Don't stack " copy" / " copy copy" on repeated duplicates.
    next.name = (base.endsWith(" copy") ? base : `${base} copy`).slice(0, 48);
  } else {
    delete next.name;
  }
  return next;
}

/** Insert `pattern` after `afterId` (or at end if missing). */
export function insertPatternAfter(clip: ClipIR, pattern: Pattern, afterId: string | undefined): void {
  const index = afterId ? clip.patterns.findIndex(entry => entry.id === afterId) : -1;
  if (index < 0) {
    clip.patterns.push(pattern);
  } else {
    clip.patterns.splice(index + 1, 0, pattern);
  }
  syncLinearArrangement(clip);
}

/** Move pattern at `fromIndex` to `toIndex` (array indices). */
export function reorderPatterns(clip: ClipIR, fromIndex: number, toIndex: number): boolean {
  if (
    fromIndex < 0
    || toIndex < 0
    || fromIndex >= clip.patterns.length
    || toIndex >= clip.patterns.length
    || fromIndex === toIndex
  ) {
    return false;
  }
  const [moved] = clip.patterns.splice(fromIndex, 1);
  if (!moved) {
    return false;
  }
  clip.patterns.splice(toIndex, 0, moved);
  syncLinearArrangement(clip);
  return true;
}

/** Splice-move one array element. Returns a new array (does not mutate). */
export function moveArrayIndex<T>(list: readonly T[], fromIndex: number, toIndex: number): T[] {
  if (
    fromIndex < 0
    || toIndex < 0
    || fromIndex >= list.length
    || toIndex >= list.length
    || fromIndex === toIndex
  ) {
    return [...list];
  }
  const next = [...list];
  const [moved] = next.splice(fromIndex, 1);
  if (moved === undefined) {
    return [...list];
  }
  next.splice(toIndex, 0, moved);
  return next;
}

/**
 * Where index `i` lands after moving `from` → `to` (same semantics as
 * `moveArrayIndex` / HTML5 list reorder).
 */
export function mapTrackIndexAfterReorder(i: number, from: number, to: number): number {
  if (i === from) {
    return to;
  }
  if (from < to) {
    if (i > from && i <= to) {
      return i - 1;
    }
  } else if (from > to) {
    if (i >= to && i < from) {
      return i + 1;
    }
  }
  return i;
}

/** Remap `/track/{n}/…` path when tracks are reordered. Leaves other paths alone. */
export function remapTrackControlPath(
  path: string,
  fromIndex: number,
  toIndex: number,
): string {
  const match = /^\/track\/(\d+)(\/[\s\S]*)?$/.exec(path);
  if (!match) {
    return path;
  }
  const oldIndex = Number(match[1]);
  if (!Number.isInteger(oldIndex)) {
    return path;
  }
  const nextIndex = mapTrackIndexAfterReorder(oldIndex, fromIndex, toIndex);
  if (nextIndex === oldIndex) {
    return path;
  }
  return `/track/${nextIndex}${match[2] ?? ""}`;
}

/** Deep-clone track cells for the arrange track clipboard. */
export function cloneTrackCells(cells: PatternCell[]): PatternCell[] {
  return structuredClone(cells);
}

/** Fit clipboard cells to a destination pattern length (truncate or pad empty). */
export function fitCellsToSteps(cells: PatternCell[], steps: number): PatternCell[] {
  const len = Math.max(1, Math.round(steps));
  if (cells.length === len) {
    return structuredClone(cells);
  }
  if (cells.length > len) {
    return structuredClone(cells.slice(0, len));
  }
  const out = structuredClone(cells);
  while (out.length < len) {
    out.push(emptyCell());
  }
  return out;
}

/** True when a track has any note / note-off / volume / fx content. */
export function trackHasContent(cells: PatternCell[] | undefined): boolean {
  if (!cells?.length) {
    return false;
  }
  for (const cell of cells) {
    if (
      cell.note !== undefined
      || cell.noteOff
      || cell.volume !== undefined
      || (cell.fx && cell.fx.length > 0)
    ) {
      return true;
    }
  }
  return false;
}

export function patternLabel(pattern: Pattern): string {
  const name = (pattern.name ?? "").trim();
  return name || pattern.id;
}

export function patternHint(pattern: Pattern): string {
  return `${pattern.id} · ${patternSteps(pattern)}`;
}
